import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subMonths, parse, compareAsc, format } from 'date-fns';
import { UserPlusIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import Chart from './shared/Chart';
import PrecheckTable, { BasePrecheckItem } from './shared/PrecheckTable';
import MerchantSubmissionsTable from './shared/MerchantSubmissionsTable';
import * as precheckService from '../services/precheckService';
import type { Precheck, PrecheckPos } from '../services/precheckService';
import { useAuth } from '../hooks/useAuth';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { role } = useAuth();
  const [precheckPoses, setPrecheckPoses] = useState<PrecheckPos[]>([]);
  const [prechecks, setPrechecks] = useState<Precheck[]>([]);
  const [merchantPrecheckPoses, setMerchantPrecheckPoses] = useState<PrecheckPos[]>([]);
  const [merchantPrechecks, setMerchantPrechecks] = useState<Precheck[]>([]);
  const [chartData, setChartData] = useState<{
    labels: string[];
    datasets: { label: string; data: number[]; backgroundColor: string }[];
  } | null>(null);

  // fetch precheckposes and prechecks
  useEffect(() => {
    const fetchPrecheckposes = async () => {
      try {
        const data = await precheckService.getAll('precheck-pos');
        setPrecheckPoses(data);
      } catch (error) {
        console.error('Error fetching PrecheckPoses:', error);
      }
    };

    const fetchPrechecks = async () => {
      try {
        const data = await precheckService.getAll('precheck');
        setPrechecks(data);
      } catch (error) {
        console.error('Error fetching Prechecks:', error);
      }
    };

    fetchPrecheckposes();
    fetchPrechecks();
  }, []);

  // fetch merchant's own prechecks
  useEffect(() => {
    if (role !== 'merchant') return;

    const fetchMerchantPrecheckposes = async () => {
      try {
        const data = await precheckService.getMerchantPrecheckPoses();
        setMerchantPrecheckPoses(data);
      } catch (error) {
        console.error('Error fetching merchant PrecheckPoses:', error);
      }
    };

    const fetchMerchantPrechecks = async () => {
      try {
        const data = await precheckService.getMerchantPrechecks();
        setMerchantPrechecks(data);
      } catch (error) {
        console.error('Error fetching merchant Prechecks:', error);
      }
    };

    fetchMerchantPrecheckposes();
    fetchMerchantPrechecks();
  }, [role]);

  // helper function to aggregate items by month
  const aggregateByMonth = (items: { createdAt: string }[]) => {
    const data: { [month: string]: number } = {};
    items.forEach((item) => {
      const monthLabel = format(new Date(item.createdAt), 'MMM yyyy'); // e.g. "Jan 2023"
      data[monthLabel] = (data[monthLabel] || 0) + 1;
    });
    return data;
  };

  // aggregate data by month
  useEffect(() => {
    const PrecheckPosesData = aggregateByMonth(precheckPoses);
    const precheckData = aggregateByMonth(prechecks);

    const allMonths = Array.from(new Set([...Object.keys(PrecheckPosesData), ...Object.keys(precheckData)]));

    const oneYearAgo = subMonths(new Date(), 12);

    const combinedDataArray = allMonths
      .map((month) => ({
        month,
        precheckPosesCount: PrecheckPosesData[month] || 0,
        precheckCount: precheckData[month] || 0,
      }))
      .sort((a, b) => compareAsc(parse(a.month, 'MMM yyyy', new Date()), parse(b.month, 'MMM yyyy', new Date())));

    const filteredDataArray = combinedDataArray.filter(
      (item) => parse(item.month, 'MMM yyyy', new Date()) >= oneYearAgo
    );

    const sortedLabels = filteredDataArray.map((i) => i.month);
    const PrecheckPosesValues = filteredDataArray.map((i) => i.precheckPosesCount);
    const precheckValues = filteredDataArray.map((i) => i.precheckCount);

    const monthlyChartData = {
      labels: sortedLabels,
      datasets: [
        {
          label: t('dashboard.posPrecheck'),
          data: PrecheckPosesValues,
          borderColor: 'rgba(207, 32, 29, 1)',
          backgroundColor: 'rgba(207, 32, 29, 0.4)',
          lineTension: 0.2,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 4,
        },
        {
          label: t('dashboard.ecomPrecheck'),
          data: precheckValues,
          borderColor: 'rgba(10, 114, 179, 1)',
          backgroundColor: 'rgba(10, 114, 179, 0.4)',
          lineTension: 0.2,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 4,
        },
      ],
    };

    setChartData(monthlyChartData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precheckPoses, prechecks]);

  // map precheckPoses and prechecks to a common format
  const combinedPrechecks: (BasePrecheckItem & { type: string })[] = [
    ...precheckPoses.map((p) => ({
      id: `POS-${p.id}`, // to avoid duplicate keys
      company: p.company,
      contact: p.contactPerson,
      email: undefined,
      createdAt: p.createdAt,
      type: 'POS',
      sourceDomain: p.sourceDomain,
      channel: p.channel,
    })),
    ...prechecks.map((p) => ({
      id: `ECOM-${p.id}`,
      company: p.genInfo.genComp,
      contact: p.genInfo.genFullName,
      email: p.genInfo.genEmail,
      createdAt: p.createdAt,
      type: 'ECOM',
      sourceDomain: p.sourceDomain,
      channel: p.channel,
    })),
  ];

  // sort the combined array by creation date descending
  const combinedPrechecksSorted = combinedPrechecks.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const renderMiddleColumns = (item: BasePrecheckItem & { type: string }) => (
    <td className="border px-2 py-2">{item.type}</td>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">{t('dashboard.title')}</h1>
        <p className="text-slate-600 mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {(role === 'admin' || role === 'domain_admin') && (
        <>
          {/* Stats Cards */}
          <div className={`grid grid-cols-1 ${role === 'admin' ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
            {/* POS Prechecks Card */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                  <CheckCircleIcon className="size-8" />
                </div>
                <span className="text-3xl font-bold">{precheckPoses.length}</span>
              </div>
              <h3 className="text-lg font-semibold mb-1">{t('dashboard.posPrecheck')}</h3>
              <p className="text-blue-100 text-sm">{t('dashboard.totalSubmissions')}</p>
            </div>

            {/* E-Commerce Prechecks Card */}
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                  <CheckCircleIcon className="size-8" />
                </div>
                <span className="text-3xl font-bold">{prechecks.length}</span>
              </div>
              <h3 className="text-lg font-semibold mb-1">{t('dashboard.ecomPrecheck')}</h3>
              <p className="text-indigo-100 text-sm">{t('dashboard.totalSubmissions')}</p>
            </div>

            {/* Quick Actions Card — admin only */}
            {role === 'admin' && (
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                    <UserPlusIcon className="size-8" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-3">{t('dashboard.quickActions')}</h3>
                <button
                  onClick={() => navigate('/add-user')}
                  className="w-full bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                >
                  {t('dashboard.addUser')}
                </button>
              </div>
            )}
          </div>

          {/* Chart Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">{t('dashboard.monthlyPrechecksCount')}</h2>
            <div className="h-96">
              <Chart data={chartData} />
            </div>
          </div>

          {/* Latest Prechecks Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900">{t('dashboard.latestPrechecks')}</h2>
              <p className="text-slate-600 text-sm mt-1">{t('dashboard.recentPrecheckSubmissions')}</p>
            </div>
            <div className="overflow-x-auto">
              <PrecheckTable
                data={combinedPrechecksSorted.slice(0, 10)}
                type="COMBINED"
                renderMiddleColumns={renderMiddleColumns}
                middleHeaders={[t('common.type')]}
              />
            </div>
          </div>
        </>
      )}

      {role === 'merchant' && (
        <>
          {/* Merchant Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button
              onClick={() => navigate('/add-precheck-pos')}
              className="group bg-white hover:bg-blue-50 rounded-2xl p-8 shadow-sm border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all text-left"
            >
              <div className="bg-blue-100 rounded-xl p-4 inline-block mb-4 group-hover:bg-blue-200 transition-colors">
                <CheckCircleIcon className="size-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">{t('dashboard.posPrecheck')}</h3>
              <p className="text-slate-600">{t('dashboard.submitNewPOS')}</p>
            </button>

            <button
              onClick={() => navigate('/add-precheck')}
              className="group bg-white hover:bg-indigo-50 rounded-2xl p-8 shadow-sm border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left"
            >
              <div className="bg-indigo-100 rounded-xl p-4 inline-block mb-4 group-hover:bg-indigo-200 transition-colors">
                <CheckCircleIcon className="size-10 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">{t('dashboard.ecomPrecheck')}</h3>
              <p className="text-slate-600">{t('dashboard.submitNewEcommerce')}</p>
            </button>
          </div>

          {/* Merchant's Submitted Prechecks */}
          <MerchantSubmissionsTable
            merchantPrecheckPoses={merchantPrecheckPoses}
            merchantPrechecks={merchantPrechecks}
          />
        </>
      )}
    </div>
  );
};

export default Dashboard;
